import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  CreditCard,
  Coins,
  Building2,
  Users,
  Settings,
  ShieldCheck,
  LogOut,
  Sun,
  Moon,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Share2,
  ToggleLeft,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { Logo } from '@/components/Logo';
import { cn } from '@/utils/cn';

const STORAGE_KEY = 'clinio-v2-admin-sidebar-collapsed';

interface NavItem {
  to: string;
  i18nKey: string;
  icon: typeof LayoutDashboard;
}

const ADMIN_NAV_ITEMS: NavItem[] = [
  { to: '/admin', i18nKey: 'admin.nav.dashboard', icon: LayoutDashboard },
  { to: '/admin/stripe', i18nKey: 'admin.nav.stripe', icon: CreditCard },
  { to: '/admin/billing', i18nKey: 'admin.nav.billing', icon: Coins },
  { to: '/admin/clinics', i18nKey: 'admin.nav.clinics', icon: Building2 },
  { to: '/admin/modules', i18nKey: 'admin.nav.modules', icon: ToggleLeft },
  { to: '/admin/users', i18nKey: 'admin.nav.users', icon: Users },
  { to: '/admin/settings', i18nKey: 'admin.nav.settings', icon: Settings },
  { to: '/admin/integrations', i18nKey: 'admin.nav.integrations', icon: Share2 },
];

export function AdminSidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // ignore
    }
  }, [collapsed]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      navigate('/sign-in', { replace: true });
    }
  };

  return (
    <aside
      className={cn(
        'flex h-screen flex-col border-r border-gray-200 bg-gray-900 transition-[width] duration-200 dark:border-gray-700',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-700 p-3">
        <div className="flex items-center gap-2">
          <Logo size={32} />
          {!collapsed && (
            <div className="flex flex-col">
              <span className="text-lg font-semibold text-white">Clinio</span>
              <span className="text-xs font-medium text-amber-400">Admin</span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="rounded p-1 text-gray-400 hover:bg-gray-800 hover:text-white"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      {/* Back to Clinic link */}
      {!collapsed && (
        <div className="border-b border-gray-700 px-2 py-3">
          <NavLink
            to="/dashboard"
            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-400 hover:bg-gray-800 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{t('admin.nav.back_to_clinic')}</span>
          </NavLink>
        </div>
      )}
      {collapsed && (
        <div className="border-b border-gray-700 px-2 py-3">
          <NavLink
            to="/dashboard"
            className="flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-800 hover:text-white"
            title={t('admin.nav.back_to_clinic')}
          >
            <ArrowLeft className="h-5 w-5" />
          </NavLink>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2">
        <ul className="space-y-1">
          {ADMIN_NAV_ITEMS.map(({ to, i18nKey, icon: Icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={to === '/admin'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition',
                    isActive
                      ? 'bg-amber-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white',
                  )
                }
                title={t(i18nKey)}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && <span className="truncate">{t(i18nKey)}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-700 p-2">
        {!collapsed && user?.email && (
          <div className="truncate px-3 py-2 text-xs text-gray-400">
            {user.email}
            <span className="ml-2 inline-flex rounded bg-amber-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white">
              Admin
            </span>
          </div>
        )}

        <NavLink
          to="/account/security"
          className={({ isActive }) =>
            cn(
              'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition',
              isActive
                ? 'bg-amber-600 text-white'
                : 'text-gray-300 hover:bg-gray-800 hover:text-white',
            )
          }
          title={t('nav.account_security')}
        >
          <ShieldCheck className="h-5 w-5" />
          {!collapsed && <span>{t('nav.account_security')}</span>}
        </NavLink>

        <button
          type="button"
          onClick={toggleTheme}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white"
          title={theme === 'dark' ? t('nav.toggle_theme_light') : t('nav.toggle_theme_dark')}
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          {!collapsed && (
            <span>
              {theme === 'dark' ? t('nav.toggle_theme_light') : t('nav.toggle_theme_dark')}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={handleSignOut}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white"
          title={t('nav.sign_out')}
        >
          <LogOut className="h-5 w-5" />
          {!collapsed && <span>{t('nav.sign_out')}</span>}
        </button>
      </div>
    </aside>
  );
}
